import { useQueryClient } from '@tanstack/react-query';
import { AnimatePresence } from 'framer-motion';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { useMutationModalContext } from '../../../contexts/MutationModalProvider';
import useBillingActions from '../../../hooks/APIHooks/finance/useBillingActions';
import { BillingHistory } from '../../../types/history';
import { fetchErrorMessage } from '../../../utils/fetchErrorMessage';
import ConsentModal from '../../CommonComps/Modals/ConsentModal';
import CreateEditBillingInfoModal from '../../CommonComps/Modals/CreateEditBillingInfoModal';

const BillingInfoModalsWrapper = () => {
  // integration of react hooks here
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  // integration of context hooks here
  const {
    data,
    showDeleteModal,
    showUpdateModal,
    setShowUpdateModal,
    setShowDeleteModal,
  } = useMutationModalContext();

  // integration of custom API hooks here
  const { deleteBillingInfoHandler } = useBillingActions('');

  // integration of tanstack query hooks here
  const queryClient = useQueryClient();

  // handler function to handle delete bill history function declared here
  const deleteBillingHistoryHandler = async () => {
    try {
      setIsDeleting(true);

      await deleteBillingInfoHandler
        .mutateAsync({ billId: (data as BillingHistory).id })
        .then((res) => {
          if (res.status === 204) {
            toast.success('Billing History Deleted!!');

            setShowDeleteModal(false);
            queryClient.invalidateQueries({
              queryKey: ['billing-info-list'],
            });
          } else {
            toast.error(
              res.data.message || 'Billing History Deletion Failed!!'
            );
          }
        })
        .catch((error) => toast.error(fetchErrorMessage(error)));
    } catch (error) {
      toast.error('Something Went Wrong!!');
    } finally {
      setIsDeleting(false);
    }
  };

  // rendering billing info modals wrapper section component here
  return (
    <section>
      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showUpdateModal ? (
          <CreateEditBillingInfoModal
            title='update'
            billingHistory={data as BillingHistory}
            handleModalClose={() => setShowUpdateModal(false)}
          />
        ) : null}
      </AnimatePresence>

      <AnimatePresence initial={false} mode='wait' onExitComplete={() => null}>
        {showDeleteModal && (
          <ConsentModal
            titleText='Are you sure?'
            subTitle={
              <>
                Are you sure you want to delete this billing history? Once you
                delete it, you can not recover it.
              </>
            }
            isLoading={isDeleting}
            yesBtnTxt='Yes'
            yesBtnVariant='danger'
            noBtnTxt='No'
            noBtnVariant='success'
            mutationHandler={deleteBillingHistoryHandler}
            handleModalClose={() => setShowDeleteModal(false)}
          />
        )}
      </AnimatePresence>
    </section>
  );
};

export default BillingInfoModalsWrapper;
